import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { toast } from "@performyard/py-components";

// Components
import DeleteDocumentModal from "./components/DeleteDocumentModal";
import { DownloadAllMyFiles, File, Folder, TotalSize } from "./components/DocumentTableRows";
import EditDocumentModal from "./components/EditDocumentModal";
import { EmbeddingTaskWatcher } from "./components/EmbeddingTaskWatcher";
import ScanWarningModal from "./components/ScanWarningModal";
import ViewDocumentModal from "./components/ViewDocumentModal";

// hooks
import {
	useDeleteDocument,
	useEditDocument,
	useToggleAiRetrieval,
	useUpdateDocumentVirusScanStatus,
} from "../../api/hooks/defs/documents";

// helpers
import { buildRows, useIsCompanyDocumentPage } from "./helpers";
// schema
import {
	DOWNLOAD_ALL_MY_FILES,
	type DocumentFile,
	type DocumentTableRow,
	type DocumentVisibility,
	type DocumentsData,
	FILE,
	FOLDER,
	type FolderState,
	MAX_CONCURRENT_EMBEDDING_TASKS,
	TOTAL_SIZE,
	type UserData,
} from "./schema";

// Map row type to component
const mapping = {
	[FOLDER]: Folder,
	[TOTAL_SIZE]: TotalSize,
	[DOWNLOAD_ALL_MY_FILES]: DownloadAllMyFiles,
	[FILE]: File,
};

const columns = [
	{
		id: "FileName",
		label: "File Name",
		isSortable: false,
		getter: (r) => mapping[r.type](r),
	},
];

// use document table hook to handle all the logic for the document table, including the modals
const useDocumentTable = (documents: DocumentsData, userData: UserData, setDocuments) => {
	const isCompanyDocumentPage = useIsCompanyDocumentPage();

	// default state of folders depending on the nav query params.
	const [folderState, setFolderState] = useState<FolderState>({
		companyDocumentsOpen: isCompanyDocumentPage,
		signedDocumentsOpen: false,
		myDocumentsOpen: !isCompanyDocumentPage,
	});
	const [deleteModalShow, setDeleteModalShow] = useState(false);
	const [editModalShow, setEditModalShow] = useState(false);
	const [viewModalShow, setViewModalShow] = useState(false);
	const [scanWarningModalShow, setScanWarningModalShow] = useState(false);
	const [selectedDocument, setSelectedDocument] = useState<DocumentFile | null>(null);

	// Track active embedding tasks with their progress: Map of documentId -> { taskId, progress }
	// Supports multiple concurrent tasks for real-time updates on all toggled documents
	const [embeddingTasks, setEmbeddingTasks] = useState<Map<string, { taskId: string; progress: number }>>(new Map());

	// Store setDocuments in a ref to avoid stale closures
	const setDocumentsRef = useRef(setDocuments);
	setDocumentsRef.current = setDocuments;

	// Store documents in a ref to avoid stale closures
	const documentsRef = useRef(documents);
	documentsRef.current = documents;

	// Initialize embeddingTasks from company documents that have active tasks (persists across page refresh)
	useEffect(() => {
		const companyFiles = documents.companyFiles ?? [];

		const initialTasks = new Map<string, { taskId: string; progress: number }>();
		for (const file of companyFiles) {
			if (file.embedding_task_id) {
				initialTasks.set(file._id, { taskId: file.embedding_task_id, progress: 0 });
			}
		}

		if (initialTasks.size > 0) {
			setEmbeddingTasks((prev) => {
				// Merge with any existing tasks (in case user toggled while loading)
				const merged = new Map(prev);
				for (const [docId, taskState] of initialTasks) {
					if (!merged.has(docId)) {
						merged.set(docId, taskState);
					}
				}
				return merged;
			});
		}
	}, [documents]);

	// Callback for embedding task updates (progress and completion)
	const handleEmbeddingTaskUpdate = useCallback(
		(documentId: string, status: string, progressPercent: number, error?: string) => {
			const isComplete = status === "completed" || status === "failed";

			if (isComplete) {
				// Update document state
				const currentDocuments = documentsRef.current;
				const newDocuments = { ...currentDocuments };

				for (const key of Object.keys(newDocuments)) {
					if (Array.isArray(newDocuments[key])) {
						newDocuments[key] = newDocuments[key].map((f) => {
							if (f._id === documentId) {
								// On failure, reset allow_ai_retrieval to false (lambda already updated DB)
								const allowAiRetrieval = status === "completed" ? f.allow_ai_retrieval : false;
								return {
									...f,
									allow_ai_retrieval: allowAiRetrieval,
									embedding_task_id: undefined,
									embedding_failed: status === "failed",
									embedding_error_type: status === "failed" ? error : undefined,
								};
							}
							return f;
						});
					}
				}

				setDocumentsRef.current(newDocuments);

				// Remove the completed task from tracking
				setEmbeddingTasks((prev) => {
					const next = new Map(prev);
					next.delete(documentId);
					return next;
				});
			} else {
				// Update progress
				setEmbeddingTasks((prev) => {
					const taskState = prev.get(documentId);
					if (!taskState) return prev;

					const next = new Map(prev);
					next.set(documentId, { ...taskState, progress: progressPercent });
					return next;
				});
			}
		},
		[]
	);

	const { mutateAsync: deleteDocumentAsync } = useDeleteDocument();
	const { mutateAsync: editDocumentAsync } = useEditDocument();
	const { mutateAsync: toggleAiRetrievalAsync } = useToggleAiRetrieval();
	const { mutateAsync: updateDocumentVirusScanStatus } = useUpdateDocumentVirusScanStatus();

	// can be optimized if we save documents in a map documents[folderName][id] = document.
	const deleteDocument = async () => {
		const id = selectedDocument?.id;
		const newDocuments = { ...documents };
		setDeleteModalShow(false);
		if (id) {
			try {
				await deleteDocumentAsync(id);

				for (const key of Object.keys(newDocuments)) {
					if (Array.isArray(newDocuments[key])) {
						newDocuments[key] = newDocuments[key].filter((f) => f._id !== id);
					}
				}

				setDocuments(newDocuments);
				toast.success("Document deleted");
			} catch (e) {
				toast.error("Error deleting document");
			}
		}
	};

	const editDocument = async (
		id: string,
		fileName: string,
		docVisibilities: DocumentVisibility[],
		tags: string[],
		allowAiRetrieval?: boolean
	) => {
		try {
			setEditModalShow(false);
			// For company documents: true = Everyone, false = Administrators Only
			const vis_to_everyone = docVisibilities.some((v) => v.value === "vis_to_everyone");
			// For user documents: checkboxes
			const vis_to_emp = docVisibilities.some((v) => v.value === "vis_to_emp");
			const vis_to_man = docVisibilities.some((v) => v.value === "vis_to_m");
			const response = await editDocumentAsync({
				id,
				fileName,
				vis_to_everyone,
				vis_to_man,
				vis_to_emp,
				tags,
				allow_ai_retrieval: allowAiRetrieval,
			});
			const newDocuments = { ...documents };

			for (const key of Object.keys(newDocuments)) {
				if (Array.isArray(newDocuments[key])) {
					newDocuments[key] = newDocuments[key].map((f) => {
						if (f._id === id) {
							// Use filename from response (handles extension preservation)
							f.filename = response.filename;
							f.tags = tags;
							// if document has vis_to_everyone field, it means it's a company document
							if (f.vis_to_everyone !== undefined) {
								f.vis_to_everyone = vis_to_everyone;
							} else {
								f.vis_to_emp = vis_to_emp;
								f.vis_to_man = vis_to_man;
							}
							if (allowAiRetrieval !== undefined) {
								f.allow_ai_retrieval = allowAiRetrieval;
							}
						}
						return f;
					});
				}
			}

			setDocuments(newDocuments);

			// If a taskId was returned, add it to the embedding tasks map for SSE tracking
			if (response.taskId) {
				setEmbeddingTasks((prev) => {
					const next = new Map(prev);
					next.set(id, { taskId: response.taskId as string, progress: 0 });
					return next;
				});
			}

			toast.success("Edited document");
		} catch (e) {
			toast.error("Error editing document");
			throw e;
		}
	};

	const toggleFolderState = (key: keyof FolderState) => {
		setFolderState((state) => ({ ...state, [key]: !state[key] }));
	};

	const handleSelectEdit = (document: DocumentFile) => {
		setSelectedDocument(document);
		setEditModalShow(true);
	};

	const handleSelectDelete = (document: DocumentFile) => {
		setSelectedDocument(document);
		setDeleteModalShow(true);
	};

	const checkVirusScanStatus = async (
		document: DocumentFile
	): Promise<{
		clean: boolean;
		pending?: boolean;
	}> => {
		// if the document is already clean or unsafe, and pending is true, we don't need to check the virus scan status
		const shouldCheckVirusScanStatus =
			!(document.clean === undefined || document.clean === null) && document.pending === false;

		if (shouldCheckVirusScanStatus) {
			return {
				clean: document.clean,
				pending: document.pending,
			};
		}

		let clean: boolean = document.clean;
		let pending: boolean = document.pending ?? false;

		try {
			// otherwise, check the virus scan results
			const { virusScanStatus } = await updateDocumentVirusScanStatus(document.id);

			switch (virusScanStatus) {
				// document was deemed clean
				case "clean":
					clean = true;
					pending = false;
					break;
				// document was deemed unsafe
				case "unsafe":
					clean = false;
					pending = false;
					break;
				// no tags were found, so we assume the scan is still pending
				case "no-tag":
					clean = false;
					pending = true;
					break;
				default:
					clean = false;
					pending = true;
					break;
			}

			return {
				clean,
				pending,
			};
			// if the call to the virus scan status endpoint fails, we'll mark the document as pending and not clean
			// the user will have the option to view the document, but will be warned that we can't guarantee it's safe
		} catch {
			clean = false;
			pending = true;
		}

		return {
			clean,
			pending,
		};
	};

	const handleViewDocument = async (document: DocumentFile) => {
		setSelectedDocument(document);

		const { clean, pending } = await checkVirusScanStatus(document);

		// update the document here with the results of the virus scan status check
		// this is necessary for displaying the correct status driven UI elements
		document.clean = clean;
		document.pending = pending;

		const isDocumentClean = clean && !pending;

		if (!isDocumentClean) {
			setScanWarningModalShow(true);
		} else {
			setViewModalShow(true);
		}
	};

	const handleWarningCancel = () => {
		setSelectedDocument(null);
		setScanWarningModalShow(false);
	};

	const handleToggleAiRetrieval = useCallback(
		async (document: DocumentFile, newValue: boolean) => {
			// Optimistically update the UI immediately
			// Clear embedding_failed when starting a new task
			const optimisticDocuments = { ...documentsRef.current };
			for (const key of Object.keys(optimisticDocuments)) {
				if (Array.isArray(optimisticDocuments[key])) {
					optimisticDocuments[key] = optimisticDocuments[key].map((f) => {
						if (f._id === document._id) {
							return {
								...f,
								allow_ai_retrieval: newValue,
								embedding_failed: undefined,
							};
						}
						return f;
					});
				}
			}
			setDocumentsRef.current(optimisticDocuments);

			try {
				const response = await toggleAiRetrievalAsync({
					id: document._id,
					fileName: document.filename,
					allow_ai_retrieval: newValue,
				});

				// If a taskId was returned, add it to the embedding tasks map for SSE tracking
				const { taskId } = response;
				if (taskId) {
					setEmbeddingTasks((prev) => {
						const next = new Map(prev);
						next.set(document._id, { taskId, progress: 0 });
						return next;
					});
				}
			} catch (e) {
				// On error when enabling, show failure icon instead of toast
				// On error when disabling, just revert to enabled state
				const errorDocuments = { ...documentsRef.current };
				for (const key of Object.keys(errorDocuments)) {
					if (Array.isArray(errorDocuments[key])) {
						errorDocuments[key] = errorDocuments[key].map((f) => {
							if (f._id === document._id) {
								return {
									...f,
									allow_ai_retrieval: !newValue,
									embedding_failed: newValue ? true : undefined,
								};
							}
							return f;
						});
					}
				}
				setDocumentsRef.current(errorDocuments);
			}
		},
		[toggleAiRetrievalAsync]
	);

	// Check if the concurrent embedding task limit has been reached
	const isEmbeddingLimitReached = embeddingTasks.size >= MAX_CONCURRENT_EMBEDDING_TASKS;

	const buildRowsProps = {
		documents,
		folderState,
		userData,
		toggleFolderState,
		handleSelectEdit,
		handleSelectDelete,
		handleViewDocument,
		handleToggleAiRetrieval,
		isCompanyDocumentPage,
		embeddingTasks,
		isEmbeddingLimitReached,
	};

	const rows: DocumentTableRow[] = useMemo(
		() => buildRows(buildRowsProps),
		[folderState, documents, userData, embeddingTasks, isEmbeddingLimitReached]
	);

	const ViewDocumentModalComponent = (
		<ViewDocumentModal
			show={viewModalShow}
			onClose={() => setViewModalShow(false)}
			userData={userData}
			document={selectedDocument}
		/>
	);
	const ScanWarningModalComponent = <ScanWarningModal show={scanWarningModalShow} onClose={handleWarningCancel} />;
	const DeleteDocumentModalComponent = (
		<DeleteDocumentModal
			show={deleteModalShow}
			onClose={() => setDeleteModalShow(false)}
			onDelete={deleteDocument}
		/>
	);
	// Check if the selected document is currently being processed
	const isSelectedDocumentProcessing = selectedDocument ? embeddingTasks.has(selectedDocument._id) : false;

	const EditDocumentModalComponent = (
		<EditDocumentModal
			show={editModalShow}
			onClose={() => setEditModalShow(false)}
			userData={userData}
			document={selectedDocument}
			onEdit={editDocument}
			isProcessing={isSelectedDocumentProcessing}
			isEmbeddingLimitReached={isEmbeddingLimitReached}
		/>
	);

	// Render a watcher component for each active embedding task
	// Each watcher subscribes to its own SSE stream and reports progress/completion
	const EmbeddingTaskWatchersComponent = (
		<>
			{Array.from(embeddingTasks.entries()).map(([documentId, { taskId }]) => (
				<EmbeddingTaskWatcher
					key={taskId}
					taskId={taskId}
					documentId={documentId}
					onUpdate={handleEmbeddingTaskUpdate}
				/>
			))}
		</>
	);

	return {
		columns,
		rows,
		ViewDocumentModalComponent,
		ScanWarningModalComponent,
		DeleteDocumentModalComponent,
		EditDocumentModalComponent,
		EmbeddingTaskWatchersComponent,
		isProcessingDocuments: embeddingTasks.size > 0,
	};
};

export default useDocumentTable;
